import { useState } from "react";
import { TabContainer, TabPane, Nav, NavItem, TabContent } from "react-bootstrap";
import { useSelector } from "react-redux";
import union from "lodash/union";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import Link from "next/link";

import { useJuneSoEventTracker } from "@stadium/services/june-analytics";
import usePopulateKitItems from "@hooks/kit/use-populate-kit";

import Multimedia from "@sharedComponents/multimedia";
import Button from "@stadium/ui/src/components/button/button";

import { getCountry } from "@selectors/country";

import { useStoreContext } from "@components/store/provider";
import KitLineItem from "@components/store/cart/kit-items/line-item";
import { getCountryFlagUrl } from "@stadium/ui/src/shared/country-flag";
import { useKitItemsContext } from "@contexts/kit-items";
import { useWorkspaceContext } from "@contexts/workspace-context";

import { getSectionData, getRemoveItemPayload } from "@components/store/cart/kit-items/utils";

import { kitDashboard } from "@constants/external-routes";
import { getCartRoute, getAllProductPageRoute } from "@constants/routes";
import {
  WAREHOUSE_ITEM,
  ON_DEMAND_ITEM,
  KIT_TYPE_US,
  KIT_TYPE_GLOBAL,
} from "@stadium/constants/kits";
import { DEFAULT_SERVICES, CREATE_BUNDLE, VIEW_AND_SEND } from "@constants/june-so";
import emptyKit from "@images/svg/empty-kit.svg";

import styles from "./main.module.scss";

const DEFAULT_OBJ = {};

const CART_TABS = [KIT_TYPE_US, KIT_TYPE_GLOBAL];

const CartFloater = ({ showItemAddedMsg = false }) => {
  const { getDashboardUrlWithCurrentWorkspaceSlug } = useWorkspaceContext();
  const { trackEvent } = useJuneSoEventTracker({
    services: DEFAULT_SERVICES,
    elementPosition: CREATE_BUNDLE,
  });

  const router = useRouter();
  const country = useSelector(getCountry);

  const { storeBasePath, getUpdatedStoreBasePath, persistedFiltersQuery } = useStoreContext();

  const { totalProductCount, bundleProducts = DEFAULT_OBJ, kitZonesMapping } = useKitItemsContext();

  const { updateItemInCart } = usePopulateKitItems();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(country?.iso.toLowerCase() || KIT_TYPE_US);

  const removeProduct = ({ item, qty }) => {
    const payload = getRemoveItemPayload([item], qty);
    const kitNumber = kitZonesMapping[item.catalogType]?.[0]?.number;

    updateItemInCart(payload, kitNumber);
  };

  const isCartEmpty = totalProductCount == 0;

  const onSaveAndExit = () => {
    if (isCartEmpty) {
      return;
    }
    window.location.href = getDashboardUrlWithCurrentWorkspaceSlug(kitDashboard);
  };

  const redirectToCart = () => {
    if (isCartEmpty) {
      return;
    }
    router.push(getCartRoute({ storeBasePath }));
  };

  const renderFooter = () => (
    <div className={styles.footerRow}>
      <div className={styles.byoWidgetCtaCol}>
        <Button
          disabled={isCartEmpty}
          customClass={`${styles.btnOutline} ${isCartEmpty ? styles.btnDisabled : ""}`}
          onClick={onSaveAndExit}
        >
          Save and Exit
        </Button>
      </div>
      <div className={styles.byoWidgetCtaCol}>
        <Button
          onClick={() => {
            trackEvent({ target: VIEW_AND_SEND });
            redirectToCart();
          }}
          customClass={`${styles.btnPrimary} ${isCartEmpty ? styles.btnDisabled : ""}`}
        >
          View and Send
        </Button>
      </div>
    </div>
  );

  const renderEmptyCart = () => (
    <section>
      {isOpen && (
        <div className={styles.emptyBundleImageContainer}>
          <Multimedia type="image" src={emptyKit} width={128} height={86} alt="Stadium" />
        </div>
      )}
      <div className={styles.emptyBundleTitle}>Your kit is empty</div>
      <div className={styles.emptyBundleSubTitle}>Add one or more items to create your kit.</div>
      {isOpen && renderFooter()}
    </section>
  );

  const renderCart = (tabKey) => {
    const { [ON_DEMAND_ITEM]: onDemandItems, [WAREHOUSE_ITEM]: warehouseItems } =
      bundleProducts[tabKey] || {};

    const lineItems = union(onDemandItems, warehouseItems);

    const { heading, countryCode, addItemsCtaText } = getSectionData(tabKey);

    const storeBasePath = getUpdatedStoreBasePath({
      countryCode,
    });

    return lineItems?.length ? (
      <div className={styles.countryTabContent}>
        {lineItems.map((item) => {
          return (
            <KitLineItem
              key={item.id}
              lineItem={item}
              handleCart={(qty) => removeProduct({ item, qty })}
              showColors={false}
              showInventory={false}
            />
          );
        })}
      </div>
    ) : (
      <div className={styles.globalBundle}>
        <div className={styles.globalBundleContent}>
          <div className={styles.noBundleHanding}>
            {heading}
            <Link
              href={getAllProductPageRoute({
                storeBasePath,
                query: persistedFiltersQuery,
              })}
              passHref
              legacyBehavior
              prefetch={false}
            >
              <span className={styles.bundleCta}>{addItemsCtaText}</span>
            </Link>
          </div>
        </div>
      </div>
    );
  };

  const renderTitle = ({ tabKey, quantity }) => (
    <div className={`${styles.countryTab} ${selectedTab === tabKey ? styles.activeLink : ""}`}>
      <div className={styles.countryTabFlag}>
        {" "}
        <Multimedia
          type="image"
          src={getCountryFlagUrl({ iso: tabKey?.toUpperCase() }, "small")}
          alt="flag"
          isNextImage={false}
          width={42}
          height={32}
        />
      </div>
      <div className={styles.countryTabLabel}>
        {tabKey} ({quantity})
      </div>
    </div>
  );

  const renderTabs = () => (
    <TabContainer {...TabContainer.defaultProps} id="cart-tab" activeKey={selectedTab}>
      <>
        <Nav {...Nav.defaultProps} className={styles.countryTabsList}>
          {CART_TABS.map((tabKey) => (
            <NavItem {...NavItem.defaultProps} key={tabKey} eventKey={tabKey} onClick={() => setSelectedTab(tabKey)}>
              {renderTitle({
                tabKey,
                quantity: bundleProducts[tabKey]?.total || 0,
              })}
            </NavItem>
          ))}
        </Nav>
        <TabContent {...TabContent.defaultProps}>
          {CART_TABS.map((tabKey) => (
            <TabPane
              {...TabPane.defaultProps}
              key={tabKey}
              eventKey={tabKey}
              className={selectedTab == tabKey ? styles.active : styles.hide}
            >
              {renderCart(tabKey)}
            </TabPane>
          ))}
        </TabContent>
      </>
    </TabContainer>
  );

  const renderCartItems = () => (
    <>
      {isOpen ? (
        <section>{renderTabs()}</section>
      ) : (
        <section className={styles.countryContainer}>
          {CART_TABS.map((tabKey) => (
            <div className={styles.countryRow} key={tabKey}>
              <div className={styles.countryFlagCol}>
                <Multimedia
                  type="image"
                  src={getCountryFlagUrl({ iso: tabKey?.toUpperCase() }, "small")}
                  width={42}
                  height={32}
                  alt="flag"
                  isNextImage={false}
                />
              </div>
              <div className={styles.countryNameCol}>
                {tabKey} ({bundleProducts[tabKey]?.total || 0})
              </div>
            </div>
          ))}
        </section>
      )}
      {renderFooter()}
    </>
  );

  return (
    <div className={`${styles.byoFloatingWidget} ${styles.openBYOWidget}`}>
      <button className={`${styles.boxTogglerButton} ${isOpen ? styles.iconDown : styles.iconUp}`}>
        <span className={styles.arrowIcon} onClick={() => setIsOpen(!isOpen)} />
      </button>
      <div className={styles.cartModalHeader}>
        {showItemAddedMsg && <span className={styles.addedMsg}>Item added!</span>}
      </div>
      {isCartEmpty ? renderEmptyCart() : renderCartItems()}
    </div>
  );
};

export default CartFloater;
